/**
 * <schedule-accordion> — in-place horizontal accordion for the UTMB schedule section.
 * CSS owns all motion (flex-grow transition keyed on [data-expanded]); this element
 * only toggles state attributes, plus one behavior CSS cannot express: scrolling
 * the opened panel into view on mobile. Spec: docs/utmb-schedule-spec.md §2 & §6.
 */
// Mirrors the section's mobile CSS scope (core/breakpoints TABLET_MAX)
const MOBILE_MEDIA_QUERY = '(max-width: 989px)';
if (!customElements.get('schedule-accordion')) {
    customElements.define('schedule-accordion', class ScheduleAccordion extends HTMLElement {
        controller = new AbortController();
        connectedCallback() {
            const { signal } = this.controller;
            this.addEventListener('click', this.onClick.bind(this), { signal });
            document.addEventListener('click', this.onDocumentClick.bind(this), { signal });
            document.addEventListener('keydown', this.onKeydown.bind(this), { signal });
        }
        disconnectedCallback() {
            this.controller.abort();
            this.controller = new AbortController();
        }
        get openTrigger() {
            return this.querySelector('[data-ref="schedule-trigger"][aria-expanded="true"]');
        }
        /**
         * Eventbrite checkout modal (Ticket 4): while it is open, Escape and
         * overlay clicks belong to Eventbrite — the accordion must not react
         * behind it. The modal iframe is created on open and removed from the
         * DOM on close (eb_widgets.js), so its presence is a reliable signal.
         */
        get eventbriteModalOpen() {
            return document.querySelector('iframe[id^="eventbrite-widget-modal-"]') !== null;
        }
        onClick(event) {
            const trigger = event.target.closest('[data-ref="schedule-trigger"]');
            if (!trigger)
                return;
            if (trigger.getAttribute('aria-expanded') === 'true') {
                this.close();
            }
            else {
                this.open(trigger);
            }
        }
        onDocumentClick(event) {
            if (!this.openTrigger)
                return;
            if (this.eventbriteModalOpen)
                return;
            // Close on any click landing outside the day tiles — including the
            // whitespace inside the row around the open tab
            const day = event.target?.closest('[data-ref="schedule-day"]');
            if (day && this.contains(day))
                return;
            this.close();
        }
        onKeydown(event) {
            if (event.key !== 'Escape')
                return;
            if (this.eventbriteModalOpen)
                return;
            const trigger = this.openTrigger;
            if (!trigger)
                return;
            this.close();
            trigger.focus();
        }
        open(trigger) {
            this.close();
            trigger.setAttribute('aria-expanded', 'true');
            const day = trigger.closest('[data-ref="schedule-day"]');
            day?.setAttribute('data-expanded', 'true');
            if (day)
                this.scrollPanelIntoView(day);
        }
        /**
         * Mobile only: after the open animation lands, minimally scroll so the
         * panel is fully visible (no-op if it already is). Waiting for the
         * grid-template-rows transitionend keeps JS free of layout math — the
         * panel's final position is only known once CSS has settled (a cross-row
         * switch moves it while the previous panel collapses).
         */
        scrollPanelIntoView(day) {
            if (!window.matchMedia(MOBILE_MEDIA_QUERY).matches)
                return;
            const panel = day.querySelector('[data-ref="schedule-panel"]');
            if (!panel)
                return;
            // Reduced motion: transitions are disabled, geometry is already final
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                panel.scrollIntoView({ block: 'nearest', behavior: 'auto' });
                return;
            }
            const onTransitionEnd = (event) => {
                // The panel also fires transitionend for opacity/visibility — wait for height
                if (event.propertyName !== 'grid-template-rows')
                    return;
                panel.removeEventListener('transitionend', onTransitionEnd);
                if (day.getAttribute('data-expanded') !== 'true')
                    return;
                panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            };
            panel.addEventListener('transitionend', onTransitionEnd, { signal: this.controller.signal });
        }
        close() {
            const trigger = this.openTrigger;
            if (!trigger)
                return;
            trigger.setAttribute('aria-expanded', 'false');
            trigger.closest('[data-ref="schedule-day"]')?.removeAttribute('data-expanded');
        }
    });
}
